<template>
  <div
    class="selectContainer"
    :class="[
      selectContainerClass,
      { 'pointer-events-none': disabled },
    ]"
    ref="container"
  >
    <!-- Label -->
    <div v-if="label" :class="['label', labelClass, { '!text-[#b7b2b5]': disabled }]">
      {{ label }}
    </div>

    <!-- Trigger -->
    <div :id="input_id" class="relative">
      <div
        class="inputCon"
        :class="[
          customClass,
          { disabled, 'border-active': openOption },
        ]"
        @click="toggleDropdown"
      >
        <div
          class="inputText truncate"
          :class="
            valInput || `${valInput}` === '0' || valInput == defaultVal
              ? 'text-[#1F1D1F]'
              : 'text-[#B7B2B5]'
          "
        >
          {{ ((valInput || `${valInput}` == "0") && nameValue) || placeholder }}
        </div>

        <div>
          <div v-if="valInput === '' || valInput === null || !allowEmpty || disabled">
            <div
              class="absolute end-4 top-1/2 -translate-y-1/2 duration-200"
              :class="{ 'rotate-180': openOption, 'text-[#B7B2B5]': disabled }"
            >
              <CaretDownIcon />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Warning -->
    <div v-if="alertWarningMessage && warningMessage" :class="['textError', {'mt-[8px]':warningMessage}]">
      {{ warningMessage }}
    </div>

    <!-- Dropdown: ไม่ใช้ teleport แล้ว -->
    <div
      v-show="openOption"
      class="dropdownCon"
      :style="dropdownPosition"
      ref="dropdown"
      @click.stop
    >
      <div
        v-for="(item, index) in optionsList"
        :key="index"
        class="box"
        :class="{
          hidden:
            (!optionView && item.optionView === false) ||
            props.hideValues.includes(keyValue ? item[keyValue] : item),
          selected: valInput === (keyValue ? item[keyValue] : item),
        }"
        @click="selectItem(item)"
      >
        {{ keyName ? item[keyName] : item }}
      </div>

      <div v-if="optionsList.length === 0" class="box" @click.stop="openOption = false">
        ไม่พบข้อมูล
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted, nextTick, onBeforeUnmount,
} from "vue";

const props = defineProps({
  selectContainerClass: String,
  label: String,
  labelClass: String,
  inputId: { type: String, default: "select" },
  options: { type: Array },
  keyValue: { type: String, default: "" },
  value: [String, Number, Object],
  placeholder: { type: String, default: "" },
  keyName: { type: String, required: false, default: "" },
  required: Boolean,
  warningMessage: { type: String, default: "" },
  disabled: Boolean,
  isCheckEmptyObject: { type: Boolean, default: false },
  lengthOptionList: { type: Number, default: 5 },
  customClass: { type: String, default: "" },
  allowEmpty: { type: Boolean, default: false },
  optionView: { type: Boolean, default: true },
  defaultVal: { type: String },
  hideValues: { type: Array, default: () => [] },
  allData: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const input_id = `select_${props.inputId}`;
const valInput = ref(props.value);
const openOption = ref(false);
const alertWarningMessage = ref(false);
const dropdownPosition = ref({});
const dropdown = ref(null);
const container = ref(null);

// --- Utils: เลือก item + toggle ---
function selectItem(item) {
  valInput.value = props.keyValue ? item[props.keyValue] : item;
}
function toggleDropdown() {
  if (props.disabled) return;
  openOption.value = !openOption.value;
}

// --- Watch value in/out ---
watch(() => props.value, (newVal) => { valInput.value = newVal; });

watch(valInput, () => {
  openOption.value = false;
  checkEmptyField();
  if (props.allData) {
    const fullObject = props.keyValue
      ? optionsList.value.find((item) => `${item[props.keyValue]}` === `${valInput.value}`)
      : valInput.value;
    emit("update:modelValue", fullObject || null);
  } else {
    emit("update:modelValue", valInput.value);
  }
});

watch(() => props.warningMessage, () => { alertWarningMessage.value = true; });

// --- คำนวณตำแหน่งแบบไม่ใช้ teleport ด้วย position: fixed + flip ---
async function updateDropdownPosition() {
  await nextTick();
  const triggerEl = document.getElementById(input_id);
  const drop = dropdown.value;
  if (!triggerEl || !drop) return;

  const rect = triggerEl.getBoundingClientRect();
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;

  // ขนาด dropdown (ชั่วคราวให้ auto เพื่อวัด height)
  drop.style.maxHeight = `calc(16px + (${props.lengthOptionList} * 40px))`;
  drop.style.visibility = "hidden";
  drop.style.position = "fixed";
  drop.style.top = "0px";
  drop.style.left = "0px";
  drop.style.width = `${rect.width}px`;
  await nextTick();
  const dropH = Math.min(drop.scrollHeight, parseFloat(getComputedStyle(drop).maxHeight));
  drop.style.visibility = "";

  // ตรวจพื้นที่ล่าง-บนเพื่อ flip
  const spaceBelow = viewportH - rect.bottom;
  const spaceAbove = rect.top;

  let top;
  if (spaceBelow >= dropH || spaceBelow >= spaceAbove) {
    // วางด้านล่าง
    top = rect.bottom;
  } else {
    // วางด้านบน (flip)
    top = Math.max(0, rect.top - dropH);
  }

  // ป้องกันล้นขอบจอด้านข้าง
  let left = rect.left;
  let width = rect.width;
  if (left + width > viewportW - 8) {
    const overflow = left + width - (viewportW - 8);
    left = Math.max(8, left - overflow);
  }
  if (left < 8) {
    width = width - (8 - left);
    left = 8;
  }

  dropdownPosition.value = {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
    maxHeight: `calc(16px + (${props.lengthOptionList} * 40px))`,
    zIndex: 9999,
    background: "#fff",
    border: "1px solid #b7b2b5",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    overflowY: "auto",
  };
}

function addGlobalListeners() {
  window.addEventListener("scroll", onScrollOrResize, true);
  window.addEventListener("resize", onScrollOrResize, true);
}
function removeGlobalListeners() {
  window.removeEventListener("scroll", onScrollOrResize, true);
  window.removeEventListener("resize", onScrollOrResize, true);
}
function onScrollOrResize() {
  if (openOption.value) updateDropdownPosition();
}

// เปิดแล้วคำนวณตำแหน่ง + ผูก listener
watch(openOption, async (isOpen) => {
  if (isOpen) {
    await updateDropdownPosition();
    addGlobalListeners();
  } else {
    removeGlobalListeners();
  }
});

// --- Options list ---
const optionsList = computed(() => {
  return props.options || [];
});

const nameValue = computed(() => {
  if (`${valInput.value}` === "0" || valInput.value) {
    const found = optionsList.value.find((el) =>
      props.keyValue
        ? `${el[props.keyValue]}` == `${valInput.value}`
        : JSON.stringify(el) === JSON.stringify(valInput.value)
    );
    return found?.[props.keyName] || "";
  }
  return "";
});

// --- Validate empty ---
function checkEmptyField() {
  if (!props.isCheckEmptyObject) {
    alertWarningMessage.value = valInput.value === "";
  } else {
    alertWarningMessage.value = Object.values(valInput.value || {}).some((v) =>
      [null, undefined, ""].includes(v)
    );
  }
}

// --- Close on outside click ---
function handleClickOutside(event) {
  const trigger = container.value;
  const drop = dropdown.value;
  if (
    trigger &&
    !trigger.contains(event.target) &&
    drop &&
    !drop.contains(event.target)
  ) {
    openOption.value = false;
  }
}

onMounted(() => {
  valInput.value = props.value;
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  removeGlobalListeners();
});
</script>

<style scoped lang="scss">
.selectContainer {
  cursor: pointer;
  width: 100%;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  .label {
    color: #1f1d1f;
    font-family: Kanit;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.1px;
  }
  .inputCon {
    position: relative;
    display: flex;
    max-height: 42px;
    padding: 10px;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    border: 1px solid #b7b2b5;
    background: #fff;
    .inputText {
      width: calc(100% - 20px);
      font-family: Kanit;
      font-size: 14px;
      font-weight: 400;
      line-height: 21px;
      letter-spacing: 0.1px;
    }
    &.disabled {
      border: 1px solid #dfdcdd;
      background: #f3f2f2;
      cursor: not-allowed;
    }
    &.border-active { border-color: #B7B2B5; }
    &.warning-border { border-color: #eb5554; }
  }
}
</style>

<style lang="scss">
/* dropdownCon จะถูกกำหนด position/top/left แบบ inline style */
.dropdownCon {
  padding: 4px;
  .box {
    cursor: pointer;
    padding: 10px;
    border-radius: 4px;
    color: #1f1d1f;
    font-family: Kanit;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0.1px;
    &:hover, &.selected { background: #fbeff8; }
    &.hidden { display: none; }
  }
}
</style>
